import './bootstrap.js'
import './override.js'
import './checkoutAddressSelectors'
import React from 'react';
import ReactDOM, {createRoot} from 'react-dom/client';
import componentRegistry from "./injector";
import {ToastContainer} from "react-toastify";
import normalizeImageSource from "./utils/normalizeImageSource";

let initializedToast = false;

function initToastContainer() {
    if (!initializedToast) {
        const container = document.createElement("div");
        document.body.appendChild(container);
        createRoot(container).render(<ToastContainer />);
        initializedToast = true;
    }
}

function isBase64(str: string) {
    try {
        return btoa(atob(str)) === str;
    } catch {
        return false;
    }
}

function getPropsFromDataset(el: HTMLElement) {
    const props: Record<string, any> = {};

    Object.keys(el.dataset).forEach((key) => {
        const value = el.dataset[key];

        if (!value) {
            props[key] = undefined;
            return;
        }

        try {
            // Parse plain JSON values first ("true", numbers, arrays, objects, etc).
            // Only fall back to base64-decoded JSON if direct JSON parsing fails.
            props[key] = JSON.parse(value);
            return;
        } catch {
            // noop
        }

        try {
            if (isBase64(value)) {
                props[key] = JSON.parse(atob(value));
                return;
            }
        } catch {
            // noop
        }

        props[key] = value;
    });

    return props;
}

function normalizeImageElementSource(el: HTMLImageElement) {
    const rawSrc = el.getAttribute('src');
    if (!rawSrc) {
        return;
    }

    const normalizedSrc = normalizeImageSource(rawSrc);
    if (normalizedSrc !== '' && normalizedSrc !== rawSrc) {
        el.setAttribute('src', normalizedSrc);
    }
}

function normalizeImageSources(root: ParentNode | HTMLElement) {
    if (root instanceof HTMLImageElement) {
        normalizeImageElementSource(root);
        return;
    }

    if (!(root instanceof Element) && !(root instanceof Document) && !(root instanceof DocumentFragment)) {
        return;
    }

    root.querySelectorAll<HTMLImageElement>('img[src]').forEach(normalizeImageElementSource);
}

// Inject all components

const roots = new WeakMap<HTMLElement, ReactDOM.Root>();
const dataSignatures = new WeakMap<HTMLElement, string>();

function getDataSignature(el: HTMLElement): string {
    return el
        .getAttributeNames()
        .filter((name) => name.startsWith('data-'))
        .sort()
        .map((name) => `${name}=${el.getAttribute(name) ?? ''}`)
        .join('|');
}

function renderComponent(el: HTMLElement) {
    const name = el.dataset.component;
    if (!name) return;

    const Component = componentRegistry[name];
    if (!Component) return;

    const signature = getDataSignature(el);
    const lastSignature = dataSignatures.get(el);
    const root = roots.get(el);

    if (root && lastSignature === signature) {
        return;
    }

    const props = getPropsFromDataset(el);

    let componentRoot = root;
    if (!componentRoot) {
        componentRoot = ReactDOM.createRoot(el);
        roots.set(el, componentRoot);
    }

    initToastContainer();
    componentRoot.render(<Component {...props} />);
    dataSignatures.set(el, signature);
}

function initLegacyAccountSidebarToggle() {
    return;
}

export const injectComponents = () => {
    normalizeImageSources(document);
    document.querySelectorAll<HTMLElement>('[data-component]').forEach(renderComponent);
    initLegacyAccountSidebarToggle();
};

const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
            const target = mutation.target;
            if (target instanceof HTMLImageElement && mutation.attributeName === 'src') {
                normalizeImageElementSource(target);
            }
            if (target instanceof HTMLElement && target.matches('[data-component]')) {
                renderComponent(target);
            }
            continue;
        }

        for (const node of mutation.addedNodes) {
            if (!(node instanceof HTMLElement)) continue;

            normalizeImageSources(node);

            if (node.matches('[data-component]')) {
                renderComponent(node);
            }

            node.querySelectorAll<HTMLElement>('[data-component]').forEach(renderComponent);
        }
    }

    initLegacyAccountSidebarToggle();
});
observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true
});

// Initial injection on DOM ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', injectComponents);
} else {
    // DOM is already ready, inject immediately
    injectComponents();
}
